import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Copy, Download, CheckCircle2, AlertCircle } from 'lucide-react';

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const PublicPix = () => {
  const { id } = useParams();
  const [cobranca, setCobranca] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`/api/public/pix/${id}`);
        setCobranca(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Cobrança não encontrada');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cobranca.brcode);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch {
      toast.error('Erro ao copiar');
    }
  };

  const handleDownloadQR = () => {
    if (!cobranca?.qr_code) return;
    const link = document.createElement('a');
    link.href = cobranca.qr_code;
    link.download = `pix-${cobranca.txid || 'cobranca'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-gray-50">
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-header.png" alt="Tenance" className="h-9" />
          </Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-[#2B7A3D] flex items-center gap-1">
            <ArrowLeft size={16} />
            Voltar ao site
          </Link>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loading ? (
          <div className="flex flex-col items-center py-16">
            <Loader2 className="w-12 h-12 animate-spin text-[#2B7A3D]" />
            <p className="text-gray-500 mt-4">Carregando pagamento...</p>
          </div>
        ) : error ? (
          <Card className="border-red-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Work Sans' }}>
                Cobrança não encontrada
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link to="/">
                <Button variant="outline">Voltar à página inicial</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-[#2B7A3D]/10 text-[#2B7A3D] px-4 py-1.5 rounded-full text-xs font-medium mb-3">
                <CheckCircle2 size={14} />
                Pagamento seguro via PIX
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: 'Work Sans' }}>
                {cobranca.cliente_nome ? `Olá, ${cobranca.cliente_nome}!` : 'Cobrança PIX'}
              </h1>
              <p className="text-gray-500 text-sm mt-1">Escaneie o QR Code ou copie o código abaixo</p>
            </div>

            <Card className="shadow-xl border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-6 text-center">
                <p className="text-xs uppercase tracking-wide opacity-90">Valor a pagar</p>
                <p className="text-4xl sm:text-5xl font-bold mt-1 tabular-nums" data-testid="public-pix-valor">
                  {formatCurrency(cobranca.valor)}
                </p>
                {cobranca.descricao && (
                  <p className="text-sm mt-2 opacity-90">{cobranca.descricao}</p>
                )}
              </div>

              <CardContent className="p-6 space-y-5">
                <div className="flex justify-center bg-gray-50 rounded-xl p-5">
                  {cobranca.qr_code && (
                    <img
                      src={cobranca.qr_code}
                      alt="QR"
                      className="w-56 h-56"
                      data-testid="public-pix-qr"
                    />
                  )}
                </div>

                <div className="text-xs text-gray-500 text-center space-y-0.5">
                  <p><span className="text-gray-400">Favorecido:</span> <span className="font-medium text-gray-700">{cobranca.favorecido_nome}</span></p>
                  <p><span className="text-gray-400">Chave PIX:</span> <span className="font-mono text-gray-700">{cobranca.chave}</span></p>
                </div>

                <Button
                  onClick={handleCopy}
                  className="w-full h-12 text-base bg-[#2B7A3D] hover:bg-[#1f5a2c] text-white shadow-lg shadow-emerald-600/20"
                  data-testid="public-pix-copy"
                >
                  {copied ? (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Código copiado!</>
                  ) : (
                    <><Copy className="w-5 h-5 mr-2" /> Copiar código PIX</>
                  )}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleDownloadQR}
                  className="w-full"
                  data-testid="public-pix-download"
                >
                  <Download size={16} className="mr-2" />
                  Baixar QR Code
                </Button>

                <details className="text-sm">
                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
                    Ver código PIX Copia e Cola
                  </summary>
                  <textarea
                    readOnly
                    value={cobranca.brcode}
                    className="mt-2 w-full p-3 text-xs font-mono bg-gray-50 border rounded-lg resize-none"
                    rows={4}
                    onClick={(e) => e.target.select()}
                  />
                </details>

                <div className="border-t pt-4 text-xs text-gray-500 space-y-2">
                  <p className="font-medium text-gray-700">Como pagar:</p>
                  <ol className="list-decimal list-inside space-y-1 pl-1">
                    <li>Abra o app do seu banco</li>
                    <li>Escolha pagar via PIX (QR Code ou Copia e Cola)</li>
                    <li>Escaneie o código acima ou cole o código copiado</li>
                    <li>Confira o valor e confirme</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            <p className="text-center text-xs text-gray-400 mt-6">
              Após o pagamento, envie o comprovante via WhatsApp para nossa equipe.
            </p>
          </>
        )}
      </main>

      <footer className="border-t border-gray-200 mt-8 py-6 text-center text-sm text-gray-500">
        © {new Date().getFullYear()} Tenance Assistência Residencial
      </footer>
    </div>
  );
};

export default PublicPix;
